import { Link, NavLink, useLocation } from "react-router";
import { toast } from "sonner";

import { useMe } from "@/lib/api";
import { cn } from "@/lib/utils";

import { getPageTitle, PERSONA_NAV } from "./nav-config";
import { usePersona } from "./persona-context";
import type { PageId } from "./types";

interface ShellProps {
  children: React.ReactNode;
}

export function Shell({ children }: ShellProps) {
  const { persona } = usePersona();
  const config = PERSONA_NAV[persona];
  const { pathname } = useLocation();
  const currentPage = pathname.split("/dashboard/")[1]?.split("/")[0] as PageId | undefined;
  const title = currentPage ? getPageTitle(persona, currentPage) : "EA SHW";

  const { data: me } = useMe();
  const displayName =
    [me?.first_name, me?.last_name].filter(Boolean).join(" ") || me?.email || config.user.name;
  const initials =
    [me?.first_name?.[0], me?.last_name?.[0]].filter(Boolean).join("").toUpperCase() ||
    config.user.initials;

  return (
    <div className="app">
      {/* Brand block */}
      <Link to="/home" className="brand">
        <img className="brand__logo" src="/brand/logo-nl.svg" alt="Universiteit Antwerpen" />
      </Link>

      {/* Top bar */}
      <header className="topbar">
        <div className="topbar__title">{title}</div>
        <div className="topbar__spacer" />
        <label className="search">
          <span aria-hidden="true">⌕</span>
          <input type="search" placeholder="Dossier of onderzoeker zoeken…" />
        </label>
        <div className="topbar__user">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              lineHeight: 1.2,
            }}
          >
            <span>{displayName}</span>
            {config.user.subtitle && (
              <span style={{ fontSize: "11px", opacity: 0.65 }}>{config.user.subtitle}</span>
            )}
          </div>
          <span className="avatar">{initials}</span>
        </div>
      </header>

      {/* Sidebar */}
      <aside className="sidebar">
        <nav>
          {config.groups.map((group) => (
            <div key={group.label}>
              <div className="nav__group-label">{group.label}</div>
              {group.items.map((item) => (
                <NavItem
                  key={item.page}
                  page={item.page}
                  icon={item.icon}
                  label={item.label}
                  badge={item.badge}
                  outOfScope={item.outOfScope}
                />
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar__footer">v0 Prototype · EA SHW</div>
      </aside>

      {/* Main */}
      <main className="main">{children}</main>
    </div>
  );
}

interface NavItemProps {
  page: PageId;
  icon: string;
  label: string;
  badge?: number;
  outOfScope?: boolean;
}

function NavItem({ page, icon, label, badge, outOfScope }: NavItemProps) {
  if (outOfScope) {
    return (
      <button
        type="button"
        className="nav__item nav__item--disabled"
        data-page={page}
        onClick={() =>
          toast("Out of Scope", { description: `${label} is buiten scope voor deze demo.` })
        }
      >
        <span className="nav__icon">{icon}</span>
        {label}
        {badge !== undefined && <span className="nav__badge">{badge}</span>}
      </button>
    );
  }

  return (
    <NavLink
      to={`/dashboard/${page}`}
      className={({ isActive }) => cn("nav__item", isActive && "is-active")}
      data-page={page}
    >
      <span className="nav__icon">{icon}</span>
      {label}
      {badge !== undefined && <span className="nav__badge">{badge}</span>}
    </NavLink>
  );
}
